import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FaXmark } from "react-icons/fa6";
import { canvas, line, muted } from "./tableStyles";

const SKIP_CLEAR = new Set(["page", "per_page"]);

function rangeTitle(label = "") {
    const cleaned = label
        .replace(/\s*(in[ií]cio|fim|inicial|final)$/i, "")
        .replace(/^data\s+de\s+/i, "")
        .replace(/^data\s+/i, "")
        .trim();
    return cleaned || "Período";
}

function groupFilters(filters) {
    const used = new Set();
    const groups = [];

    filters.forEach((filter) => {
        if (used.has(filter.field)) return;

        if (filter.type === "datepicker" && filter.field.endsWith("_start")) {
            const prefix = filter.field.slice(0, -6);
            const end = filters.find((item) => item.field === `${prefix}_end`);
            if (end) {
                used.add(filter.field);
                used.add(end.field);
                groups.push({ type: "range", start: filter, end, title: rangeTitle(filter.label) });
                return;
            }
        }

        if (filter.type === "datepicker" && filter.field.endsWith("_from")) {
            const prefix = filter.field.slice(0, -5);
            const end = filters.find((item) => item.field === `${prefix}_to`);
            if (end) {
                used.add(filter.field);
                used.add(end.field);
                groups.push({ type: "range", start: filter, end, title: rangeTitle(filter.label) });
                return;
            }
        }

        used.add(filter.field);
        groups.push({ type: "single", filter });
    });

    return groups;
}

function DateField({ label, value, onChange, minDate, maxDate }) {
    return (
        <DatePicker
            label={label}
            value={value ? dayjs(value) : null}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            onChange={(date) => onChange(date?.isValid() ? date.format("YYYY-MM-DD") : "")}
            format="DD/MM/YYYY"
            slotProps={{
                textField: { size: "small", fullWidth: true },
                field: { clearable: true },
            }}
        />
    );
}

function FilterField({ filter, values, onChange }) {
    if (filter.type === "select") {
        return (
            <TextField
                select
                fullWidth
                label={filter.label}
                value={values[filter.field] ?? ""}
                onChange={(event) => onChange(filter.field, event.target.value)}
                size="small"
            >
                <MenuItem value="">Todos</MenuItem>
                {(filter.options || []).map((opt) => (
                    <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
                        {opt.label ?? opt}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (filter.type === "datepicker") {
        return (
            <DateField
                label={filter.label}
                value={values[filter.field]}
                onChange={(value) => onChange(filter.field, value)}
            />
        );
    }

    return (
        <TextField
            type={filter.type === "number" ? "number" : "text"}
            fullWidth
            label={filter.label}
            value={values[filter.field] ?? ""}
            onChange={(event) => onChange(filter.field, event.target.value)}
            size="small"
        />
    );
}

export default function CustomFilterBar({
    filters = [],
    values = {},
    onChange,
    search = null,
    actions = null,
}) {
    if (!filters.length && !search && !actions) return null;

    const groups = groupFilters(filters);
    const hasActiveFilter = filters.some((filter) => {
        if (SKIP_CLEAR.has(filter.field)) return false;
        const value = values[filter.field];
        return value !== undefined && value !== null && value !== "";
    });

    const handleClear = () => {
        filters.forEach((filter) => {
            if (!SKIP_CLEAR.has(filter.field)) {
                onChange(filter.field, "");
            }
        });
    };

    return (
        <Box sx={styles.wrap}>
            {(search || actions) && (
                <Box sx={{ ...styles.toolbar, pb: groups.length ? 0 : 2 }}>
                    {search}
                    {actions}
                </Box>
            )}

            {groups.length > 0 && (
                <Box sx={styles.grid}>
                    {groups.map((group) => {
                        if (group.type === "range") {
                            return (
                                <Box key={group.start.field} sx={styles.range}>
                                    <Typography sx={styles.rangeTitle}>{group.title}</Typography>
                                    <Box sx={styles.rangeFields}>
                                        <DateField
                                            label="De"
                                            value={values[group.start.field]}
                                            maxDate={values[group.end.field]}
                                            onChange={(value) => onChange(group.start.field, value)}
                                        />
                                        <DateField
                                            label="Até"
                                            value={values[group.end.field]}
                                            minDate={values[group.start.field]}
                                            onChange={(value) => onChange(group.end.field, value)}
                                        />
                                    </Box>
                                </Box>
                            );
                        }

                        return (
                            <Box key={group.filter.field} sx={styles.single}>
                                <FilterField filter={group.filter} values={values} onChange={onChange} />
                            </Box>
                        );
                    })}
                </Box>
            )}

            {hasActiveFilter && (
                <Box sx={styles.clearRow}>
                    <Button
                        size="small"
                        onClick={handleClear}
                        startIcon={<FaXmark />}
                        sx={styles.clearButton}
                    >
                        Limpar filtros
                    </Button>
                </Box>
            )}
        </Box>
    );
}

const styles = {
    wrap: {
        bgcolor: canvas,
        borderBottom: `1px solid ${line}`,
    },
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        pt: 2,
        pb: 0,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: 1.5,
        px: 2,
        py: 2,
        alignItems: "end",
    },
    single: {
        minWidth: 0,
    },
    range: {
        gridColumn: { xs: "1 / -1", sm: "span 2" },
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
    },
    rangeTitle: {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: muted,
        textAlign: "left",
    },
    rangeFields: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        minWidth: 0,
    },
    clearRow: {
        px: 2,
        pb: 1.5,
        mt: -0.5,
    },
    clearButton: {
        color: muted,
        textTransform: "none",
        "&:hover": { color: "#0B0A08" },
    },
};
